import React, { useState, useRef } from 'react';
import { RotateCcwIcon, RotateCwIcon, ZoomInIcon, ZoomOutIcon, MaximizeIcon, MinimizeIcon, ScissorsIcon, GitMergeIcon, MoveIcon, LayersIcon, Maximize2Icon, LassoIcon, MousePointerIcon, RefreshCwIcon, HighlighterIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';
export function DocumentViewer({
  documentUrl,
  extractedData,
  isExceptionMode = false
}) {
  const [rotation, setRotation] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [activeTool, setActiveTool] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // In a real app, this would be determined from the document
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0
  });
  const [viewPosition, setViewPosition] = useState({
    x: 0,
    y: 0
  });
  const imageRef = useRef(null);
  // Rotate document left (counter-clockwise)
  const rotateLeft = () => {
    setRotation(prev => (prev - 90) % 360);
  };
  // Rotate document right (clockwise)
  const rotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };
  // Zoom in
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 200));
  };
  // Zoom out
  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 50));
  };
  // Reset zoom
  const resetZoom = () => {
    setZoomLevel(100);
    setViewPosition({
      x: 0,
      y: 0
    });
  };
  // Fit to width
  const fitToWidth = () => {
    // In a real implementation, this would calculate the appropriate zoom level
    // based on the document width and container width
    setZoomLevel(100);
    setViewPosition({
      x: 0,
      y: 0
    });
  };
  // Handle tool selection
  const handleToolSelect = tool => {
    setActiveTool(activeTool === tool ? null : tool);
  };
  // Navigate to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  // Navigate to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  // Handle mouse down for panning
  const handleMouseDown = e => {
    if (activeTool === 'pan') {
      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY
      });
    }
  };
  // Handle mouse move for panning
  const handleMouseMove = e => {
    if (isDragging && activeTool === 'pan') {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setViewPosition({
        x: viewPosition.x + dx,
        y: viewPosition.y + dy
      });
      setDragStart({
        x: e.clientX,
        y: e.clientY
      });
    }
  };
  // Handle mouse up for panning
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4750">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-4751">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4752">
          Document Viewer
        </h3>
        <div className="flex space-x-1" data-id="element-4753">
          <span className="text-sm text-gray-500" data-id="element-4754">
            Page {currentPage} of {totalPages}
          </span>
          <button onClick={prevPage} disabled={currentPage === 1} className={`p-1 rounded-md ${currentPage === 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-4755">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-4756" />
          </button>
          <button onClick={nextPage} disabled={currentPage === totalPages} className={`p-1 rounded-md ${currentPage === totalPages ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-4757">
            <ChevronRightIcon className="h-5 w-5" data-id="element-4758" />
          </button>
        </div>
      </div>
      {/* Toolbar */}
      <div className="px-4 py-2 border-b border-gray-200 flex flex-wrap gap-1" data-id="element-4759">
        {/* Rotation Controls */}
        <div className="flex space-x-1 mr-3" data-id="element-4760">
          <button onClick={rotateLeft} className="p-1 rounded-md text-gray-500 hover:bg-gray-100" title="Rotate Left" data-id="element-4761">
            <RotateCcwIcon className="h-5 w-5" data-id="element-4762" />
          </button>
          <button onClick={rotateRight} className="p-1 rounded-md text-gray-500 hover:bg-gray-100" title="Rotate Right" data-id="element-4763">
            <RotateCwIcon className="h-5 w-5" data-id="element-4764" />
          </button>
        </div>
        {/* Zoom Controls */}
        <div className="flex space-x-1 mr-3" data-id="element-4765">
          <button onClick={zoomOut} className="p-1 rounded-md text-gray-500 hover:bg-gray-100" title="Zoom Out" data-id="element-4766">
            <ZoomOutIcon className="h-5 w-5" data-id="element-4767" />
          </button>
          <span className="inline-flex items-center px-2 text-sm text-gray-500" data-id="element-4768">
            {zoomLevel}%
          </span>
          <button onClick={zoomIn} className="p-1 rounded-md text-gray-500 hover:bg-gray-100" title="Zoom In" data-id="element-4769">
            <ZoomInIcon className="h-5 w-5" data-id="element-4770" />
          </button>
          <button onClick={resetZoom} className="p-1 rounded-md text-gray-500 hover:bg-gray-100" title="Reset Zoom" data-id="element-4771">
            <MaximizeIcon className="h-5 w-5" data-id="element-4772" />
          </button>
          <button onClick={fitToWidth} className="p-1 rounded-md text-gray-500 hover:bg-gray-100" title="Fit to Width" data-id="element-4773">
            <Maximize2Icon className="h-5 w-5" data-id="element-4774" />
          </button>
        </div>
        {/* Document Tools */}
        <div className="flex space-x-1 mr-3" data-id="element-4775">
          <button onClick={() => handleToolSelect('pan')} className={`p-1 rounded-md ${activeTool === 'pan' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Pan" data-id="element-4776">
            <MoveIcon className="h-5 w-5" data-id="element-4777" />
          </button>
          <button onClick={() => handleToolSelect('split')} className={`p-1 rounded-md ${activeTool === 'split' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Split Pages" data-id="element-4778">
            <ScissorsIcon className="h-5 w-5" data-id="element-4779" />
          </button>
          <button onClick={() => handleToolSelect('merge')} className={`p-1 rounded-md ${activeTool === 'merge' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Merge Pages" data-id="element-4780">
            <GitMergeIcon className="h-5 w-5" data-id="element-4781" />
          </button>
          <button onClick={() => handleToolSelect('reorder')} className={`p-1 rounded-md ${activeTool === 'reorder' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Reorder Pages" data-id="element-4782">
            <LayersIcon className="h-5 w-5" data-id="element-4783" />
          </button>
        </div>
        {/* Extraction Tools */}
        <div className="flex space-x-1" data-id="element-4784">
          <button onClick={() => handleToolSelect('lasso')} className={`p-1 rounded-md ${activeTool === 'lasso' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Lasso Extract" data-id="element-4785">
            <LassoIcon className="h-5 w-5" data-id="element-4786" />
          </button>
          <button onClick={() => handleToolSelect('pointer')} className={`p-1 rounded-md ${activeTool === 'pointer' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Click to Extract" data-id="element-4787">
            <MousePointerIcon className="h-5 w-5" data-id="element-4788" />
          </button>
          <button onClick={() => handleToolSelect('reocr')} className={`p-1 rounded-md ${activeTool === 'reocr' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Re-Process Region" data-id="element-4789">
            <RefreshCwIcon className="h-5 w-5" data-id="element-4790" />
          </button>
          <button onClick={() => handleToolSelect('highlight')} className={`p-1 rounded-md ${activeTool === 'highlight' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:bg-gray-100'}`} title="Highlight Mapping" data-id="element-4791">
            <HighlighterIcon className="h-5 w-5" data-id="element-4792" />
          </button>
        </div>
      </div>
      {/* Document Display */}
      <div className="p-4 overflow-auto bg-gray-100 flex items-center justify-center" style={{
      height: isExceptionMode ? '45vh' : '50vh'
    }} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} data-id="element-4793">
        <div style={{
        transform: `rotate(${rotation}deg) scale(${zoomLevel / 100})`,
        transformOrigin: 'center center',
        transition: 'transform 0.2s ease',
        cursor: activeTool === 'pan' ? 'grab' : 'default',
        position: 'relative',
        left: `${viewPosition.x}px`,
        top: `${viewPosition.y}px`
      }} data-id="element-4794">
          <img ref={imageRef} src={documentUrl} alt="Document" className="max-w-full h-auto" style={{
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }} data-id="element-4795" />
          {/* Overlay for highlighting extracted fields (shown when highlight tool is active) */}
          {activeTool === 'highlight' && <div className="absolute inset-0" data-id="element-4796">
              {/* Example highlighting for invoice number */}
              <div className="absolute border-2 border-indigo-500 bg-indigo-100 bg-opacity-30 rounded-sm" style={{
            top: '12%',
            right: '20%',
            width: '15%',
            height: '3%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-4797">
                <span className="text-xs font-medium text-indigo-800 bg-white px-1 rounded" data-id="element-4798">
                  Invoice #
                </span>
              </div>
              {/* Example highlighting for date */}
              <div className="absolute border-2 border-indigo-500 bg-indigo-100 bg-opacity-30 rounded-sm" style={{
            top: '12%',
            left: '20%',
            width: '15%',
            height: '3%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-4799">
                <span className="text-xs font-medium text-indigo-800 bg-white px-1 rounded" data-id="element-4800">
                  Date
                </span>
              </div>
              {/* Example highlighting for vendor */}
              <div className="absolute border-2 border-indigo-500 bg-indigo-100 bg-opacity-30 rounded-sm" style={{
            top: '8%',
            left: '10%',
            width: '25%',
            height: '3%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-4801">
                <span className="text-xs font-medium text-indigo-800 bg-white px-1 rounded" data-id="element-4802">
                  Vendor
                </span>
              </div>
              {/* Example highlighting for total */}
              <div className="absolute border-2 border-indigo-500 bg-indigo-100 bg-opacity-30 rounded-sm" style={{
            bottom: '20%',
            right: '10%',
            width: '15%',
            height: '3%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-4803">
                <span className="text-xs font-medium text-indigo-800 bg-white px-1 rounded" data-id="element-4804">
                  Total
                </span>
              </div>
            </div>}
        </div>
      </div>
      {/* Tool Instructions */}
      {activeTool && <div className="px-4 py-3 bg-indigo-50 border-t border-indigo-100" data-id="element-4805">
          <p className="text-sm text-indigo-700" data-id="element-4806">
            {activeTool === 'pan' && 'Click and drag to pan the document.'}
            {activeTool === 'lasso' && 'Draw around text to extract it.'}
            {activeTool === 'pointer' && 'Click on text to extract it, then assign to a field.'}
            {activeTool === 'reocr' && 'Select an area to re-run Document Intelligence on that region.'}
            {activeTool === 'highlight' && 'Showing extracted fields and their source on the document.'}
            {activeTool === 'split' && 'Click where you want to split the document into separate pages.'}
            {activeTool === 'merge' && 'Select pages to merge into a single document.'}
            {activeTool === 'reorder' && 'Drag and drop thumbnails to reorder pages.'}
          </p>
        </div>}
    </div>;
}